import { useState, useMemo } from 'react';
import './DBApp.css';
import artistOrder from "./artists.json";
import catalogue from './CompleteList.json';
import AlbumPlayer from './AudioPlayer.js';
import playLists from './playlists.json';

function DBApp({listeningChoice}) {
  const [cabinet1State, setCabinet1State] = useState(false);
  const [cabinet2State, setCabinet2State] = useState(false);
  const numberOfShuffledSongs = 50;

  //                 Collect The Titles                  //
  const artistAlbumKeys = Array.from(new Set(catalogue.map((item) => (item["artist"] + '-' + item["album"])).sort()));
  const tracksByGenre = Object.groupBy(catalogue, item => item.genre);
  const [albumSelection, setAlbumSelection] = useState(artistAlbumKeys[16]);  
  const [genreSelection, setGenreSelection] = useState(Object.keys(tracksByGenre)[1]);
  const [playListSelection, setPlayListSelection] = useState(Object.keys(playLists)[0]);
  
  //   Catalogue All The Albums and sort them   //
  const artistBins = useMemo(() => {
    let artistAlbumArray = {};
    catalogue.forEach((value) => {
      let currentArtist = value.artist;
      let currentAlbum = value.album;
      let currentAlbumCover = value.artistFolder + '/' + value.albumFolder + '/' + value.cover;
      let currentArtistAlbumKey = (value.artist + '-' + value.album);
      if (!artistAlbumArray[currentArtist]) {
        artistAlbumArray[currentArtist] = [];
      } else if (!artistAlbumArray[currentArtist][currentArtistAlbumKey]) {
        artistAlbumArray[currentArtist][currentArtistAlbumKey] = [currentAlbum, currentAlbumCover];
      };
    });

    return artistAlbumArray;

  },[]);

  //   Catalogue All The Tracks and sort them   //
  const artistAlbumTracks = useMemo(() => {
    let artistTrackArray = {};
    catalogue.forEach((value) => {
      let currentArtistAlbumKey = (value.artist + '-' + value.album);
      if (!artistTrackArray[currentArtistAlbumKey]) {
        artistTrackArray[currentArtistAlbumKey] = [];
      }
      artistTrackArray[currentArtistAlbumKey].push(value);
    });

    return artistTrackArray;

  }, []);  

  function shuffler(arr, count) {
    // Make a shallow copy so we don't mutate the original array
    const copy = [...arr];

    // Use Fisher-Yates shuffle for efficiency
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }

    // Return the first `count` items
    return copy.slice(0, count);
  }

  const shuffled = useMemo(() => shuffler(catalogue, numberOfShuffledSongs), []);

  
  const albumSelected = (selection) => {
    setAlbumSelection(selection); 
    if (selection) {
        setCabinet1State(false);
    }
  };

  const cabinet1Change = () => {
    if (cabinet1State) {
      setCabinet1State(false);
    } else {
      setCabinet1State(true)
    }
  };

  const genreSelected = (selection) => {
    setGenreSelection(selection); 
    if (selection) {
        setCabinet2State(false);
    }
  };

  const playListSelected = (selection) => {
    setPlayListSelection(selection); 
    if (selection) {
        setCabinet2State(false);
    }
  };

  const cabinet2Change = () => {
    if (cabinet2State) {
      setCabinet2State(false);
    } else {
      setCabinet2State(true)
    }
  };

  const randomSelection = () => {
    const numberOfAlbums = artistAlbumKeys.length;
    const newAlbum = Math.floor(Math.random() * numberOfAlbums);
    setAlbumSelection(artistAlbumKeys[newAlbum]);
  }  

  const playerSongList = useMemo(() => {
      if (listeningChoice === 'Album') {
        return artistAlbumTracks[albumSelection];
      };
      if (listeningChoice === 'Playlist') {
        return playLists[playListSelection];
      };
      if (listeningChoice === 'Random') {
        return shuffled;
      };
      if (listeningChoice === 'Genre') {
        return tracksByGenre[genreSelection];
      };

    }, [albumSelection, artistAlbumTracks, playListSelection, listeningChoice, shuffled, tracksByGenre, genreSelection]
  );

  const reversePlayerSongList = useMemo(() => playerSongList.toReversed(), [playerSongList]);
  const [songSelection, setSongSelection] = useState(0);
  const [ flipOrder, setFlipOrder ] = useState(false);
  const currentSongList = useMemo(() => {
    if (flipOrder) {
        return reversePlayerSongList;
    } else {
        return playerSongList;
    };
  }, [flipOrder, playerSongList, reversePlayerSongList]);

  const currentPlayList = useMemo(() => {
    let songListArray = [];
    currentSongList.forEach((value) => {
      let newFormat = {
        "track":value.track, 
        "song":value.song, 
        "artist":value.artist, 
        "album":value.album, 
        "location": (value.artistFolder + '/' + value.albumFolder), 
        "file":value.file, 
        "cover":value.cover,
        "genre":value.genre,
        "bpm":value.bpm
      }
      songListArray.push(newFormat);
    });

    return songListArray;
    }, [currentSongList]
  );

  const playThis = (songNumber) => {
      setSongSelection(songNumber)
  };
  
  const [currentShuffledSong, setCurrentShuffledSong]  = useState(0);

  const flipTheList = () => {
      setFlipOrder(!flipOrder);
  }

  return (
    <div className="playerSystem">
      <div className="list-selector">
        {
          (listeningChoice === 'Album') && <div className="cabinet-controller">
            <button className="flatButton" id="cabinet-latch" onClick={cabinet1Change}>Album Selector</button>
          </div>
        }
        {cabinet1State && (listeningChoice === 'Album') &&
          <div id="list-cabinet">
            {
              artistOrder.map((artist) => 
                (
                  <div  className="artist" key={artist.trim()}>
                    <h1>{artist}</h1>
                    <div className="artist-albums">
                      {
                        Object.keys(artistBins[artist]).map((album) => 
                          <div key={album.trim()} className="album-display">
                            <img className="thumbnail" src={`./music/${artistBins[artist][album][1]}`} alt="Album cover" onClick={() => albumSelected(album)}/>
                            <p>
                              {artistBins[artist][album][0]}
                            </p>
                          </div>
                        )
                      }
                    </div>
                  </div>
                )
              )
            }
            <button className="flatButton" onClick={randomSelection}>Randomize</button>
          </div>
        }
        {
          (listeningChoice === 'Genre') && <div className="cabinet-controller">
            <button className="flatButton" id="cabinet-latch" onClick={cabinet2Change}>Genre Selector</button>
          </div>
        }
        {cabinet2State && (listeningChoice === 'Genre') &&
          <div id="list-cabinet">
            {
              <div className="artist-albums">
                {
                  Object.keys(tracksByGenre).map((list) => 
                    <div key={list} className="album-display">
                      <button className="flatButton" onClick={() => genreSelected(list)}>{list}</button>
                    </div>
                  )
                }
              </div>
            }
          </div>
        }
        {
          (listeningChoice === 'Playlist') && <div className="cabinet-controller">
            <button className="flatButton" id="cabinet-latch" onClick={cabinet2Change}>Playlist Selector</button>
          </div>
        }
        {cabinet2State && (listeningChoice === 'Playlist') &&
          <div id="list-cabinet">
            {
              <div className="artist-albums">
                {
                  Object.keys(playLists).map((list) => 
                    <div key={list.trim()} className="album-display">
                      <button className="flatButton" onClick={() => playListSelected(list)}>{list}</button>
                    </div>
                  )
                }
              </div>
            }
          </div>
        }
      </div>
      <div className="media-player">
        {(listeningChoice === 'Album') && <AlbumPlayer albums={artistAlbumKeys} albumSelection={albumSelection} setAlbumSelection={setAlbumSelection} songSelection={songSelection} setSongSelection={setSongSelection} songs={currentPlayList} />}
        {(listeningChoice === 'Random') && <AlbumPlayer albums="None" albumSelection="None" setAlbumSelection="None" songSelection={currentShuffledSong} setSongSelection={setCurrentShuffledSong} songs={currentPlayList} />}
        {(listeningChoice === 'Genre') && <AlbumPlayer albums={Object.keys(tracksByGenre)} albumSelection={genreSelection} setAlbumSelection={setGenreSelection} songSelection={songSelection} setSongSelection={setSongSelection} songs={currentPlayList} />}
        {(listeningChoice === 'Playlist') && <AlbumPlayer albums={Object.keys(playLists)} albumSelection={playListSelection} setAlbumSelection={setPlayListSelection} songSelection={songSelection} setSongSelection={setSongSelection} songs={currentPlayList} />}
      </div>      
      <div className="songListDisplay">
          {<div className="playlist">
              <h2>{(listeningChoice === 'Album') && albumSelection.replace("-", " - ")}{(listeningChoice === 'Random') && "Random"}{(listeningChoice === 'Playlist') && "Playlist - " + playListSelection}</h2>
              <button id="reverse" onClick={() => flipTheList()}>Reverse Order</button>
              <ul id="song-list">
                  {currentSongList.map((track, index) => <li key={track.song} onClick={() => playThis(index)}>{track.track} - {track.song}</li>)}
              </ul>
          </div>}
        </div>
    </div>
  );
}

export default DBApp;
